// src/helpers/MarkdownRenderer.tsx
import * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import TableWithExport from "../components/markdown/TableWithExport";
import CodeBlock from "../components/markdown/CodeBlock";
import { CitationBubble } from "../components/chat/CitationBubble";
import { DocumentCitationBubble } from "../components/chat/DocumentCitationBubble";
import { PlaybookCitationBubble } from "../components/chat/PlaybookCitationBubble";
import { CatylexCitationBubble } from "../components/chat/CatylexCitationBubble";
import { useDocumentCitations } from "../contexts/DocumentCitationContext";
import { isPlaybookCitation, isCatylexCitation } from "../interfaces/documentCitationTypes";
import { createLogger } from "../utils/logger";
import { rehypeGroupDocCites } from "./rehypeGroupDocCites";
import { rehypeGroupWebCites } from "./rehypeGroupWebCites";
import type { WebCiteSource } from "./rehypeGroupWebCites";
import { rehypeStripReservedProps } from "./rehypeStripReservedProps";
import type { CitationGroup } from "./citationGroups";
import { groupCitations } from "./citationGroups";

const renderCitationGroup = (group: CitationGroup | undefined): React.ReactNode => {
  if (!group) return null;
  const [citationRef] = group.refs;
  const groupRefs = group.refs.length > 1 ? group.refs : undefined;

  switch (group.kind) {
    case "playbook":
      return <PlaybookCitationBubble citationRef={citationRef} groupRefs={groupRefs} />;
    case "catylex":
      return <CatylexCitationBubble citationRef={citationRef} groupRefs={groupRefs} />;
    default:
      return <DocumentCitationBubble citationRef={citationRef} groupRefs={groupRefs} />;
  }
};

/**
 * Routes a <doc-cite/> tag to the appropriate bubble based on citation source.
 * Lives as a component (not inline in the components map) because hooks
 * can't run inside the components-object factory.
 *
 * `groupRefs` is set when rehypeGroupDocCites merged a run of adjacent tags
 * into this one; the run renders one chip per citation type. A single tag
 * takes the original path unchanged.
 */
const DocCiteRouter: React.FC<{ citationRef: string; groupRefs?: string[] }> = ({
  citationRef,
  groupRefs,
}) => {
  const { lookupCitation } = useDocumentCitations();

  if (groupRefs && groupRefs.length > 1) {
    const groups = groupCitations(groupRefs, lookupCitation);

    // One slot per citation type as separate children, not a keyed list: the
    // first slot is then matched to the chip this tag rendered before the run
    // grew, so that chip is updated in place rather than remounted.
    return (
      <>
        {renderCitationGroup(groups[0])}
        {renderCitationGroup(groups[1])}
        {renderCitationGroup(groups[2])}
      </>
    );
  }
  const cit = lookupCitation(citationRef);
  if (cit && isPlaybookCitation(cit)) {
    return <PlaybookCitationBubble citationRef={citationRef} />;
  }
  if (cit && isCatylexCitation(cit)) {
    return <CatylexCitationBubble citationRef={citationRef} />;
  }
  return <DocumentCitationBubble citationRef={citationRef} />;
};

const log = createLogger("MarkdownRenderer");

type MarkdownRendererProps = {
  children: string;
  className?: string;
};

/**
 * Sources of a merged run of web citations, from the JSON "data-sources"
 * attribute rehypeGroupWebCites sets; undefined for a single citation.
 */
const parseWebCiteGroup = (value: unknown): WebCiteSource[] | undefined => {
  if (typeof value !== "string") return undefined;
  try {
    const parsed: unknown = JSON.parse(value);

    return Array.isArray(parsed) && parsed.length > 1 ? (parsed as WebCiteSource[]) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Convert self-closing <cite .../> tags to proper <cite ...></cite> format.
 * HTML5 doesn't recognize <cite> as a void element, so self-closing syntax
 * causes parsing issues where citations get moved to the end of blocks.
 */
const preprocessCiteTags = (content: string): string => {
  // Match <cite ...attributes.../> and convert to <cite ...attributes></cite>
  return content.replace(/<cite\s+([^>]*?)\/>/gi, "<cite $1></cite>");
};

/**
 * Convert self-closing <doc-cite .../> tags to proper <doc-cite ...></doc-cite> format.
 * HTML5 doesn't recognize <doc-cite> as a void element, so self-closing syntax
 * causes parsing issues. Same approach as preprocessCiteTags.
 */
const preprocessDocCiteTags = (content: string): string => {
  return content.replace(/<doc-cite\s+([^>]*?)\/>/gi, "<doc-cite $1></doc-cite>");
};

type MdastNode = {
  type: string;
  lang?: string | null;
  value?: string;
  children?: MdastNode[];
};

const collectText = (node: MdastNode): string => {
  if (typeof node.value === "string") return node.value;
  return (node.children ?? []).map(collectText).join("");
};

const CHIP_MAX_LENGTH = 50;
// Punctuation that should hug the chip without an inserted space
const OPENERS = /[([{]$/;
const CLOSERS = /^[)\]},.;:!?]/;
const SENTENCE_TERMINAL = /[.!?]$/;
const CONTINUATION = /^[a-z)\]},.;:!?]/;

// A fenced/indented block holding just one short line with no language tag is
// almost always the model setting off a term or identifier, not real
// preformatted content.
const isChippableCodeBlock = (node: MdastNode): boolean => {
  if (node.type !== "code" || node.lang) return false;
  const value = (node.value ?? "").trim();
  return value.length > 0 && value.length <= CHIP_MAX_LENGTH && !value.includes("\n");
};

/**
 * The LLM sometimes fences a short identifier mid-sentence (e.g. "I will use
 * the ```playbook_tool``` to..."), which markdown parses as paragraph + code
 * block + paragraph — tearing the sentence across three rows. This remark
 * plugin converts such blocks to inline code and, when the neighbouring
 * paragraphs read as one interrupted sentence, stitches them back together.
 * A short block between two complete sentences stays on its own row.
 */
const remarkInlineShortCodeBlocks = () => {
  const visit = (parent: MdastNode): void => {
    const children = parent.children;
    if (!children) return;
    for (let i = 0; i < children.length; i++) {
      const node = children[i];
      if (node.children) visit(node);

      // Malformed model output (e.g. a stray ``` line) can produce a code
      // block with no content — drop it rather than render an empty frame.
      if (node.type === "code" && (node.value ?? "").trim() === "") {
        children.splice(i, 1);
        i -= 1;
        continue;
      }

      if (!isChippableCodeBlock(node)) continue;

      const chip: MdastNode = { type: "inlineCode", value: (node.value ?? "").trim() };
      const prev = children[i - 1];
      const next = children[i + 1];
      const prevText = prev?.type === "paragraph" ? collectText(prev).trimEnd() : "";
      const nextText = next?.type === "paragraph" ? collectText(next).trimStart() : "";
      const prevIsOpenSentence = prevText !== "" && !SENTENCE_TERMINAL.test(prevText);
      const nextIsContinuation = nextText !== "" && CONTINUATION.test(nextText);

      if (prevIsOpenSentence && prev.children) {
        if (!OPENERS.test(prevText)) prev.children.push({ type: "text", value: " " });
        prev.children.push(chip);
        if (nextIsContinuation && next.children) {
          if (!CLOSERS.test(nextText)) prev.children.push({ type: "text", value: " " });
          prev.children.push(...next.children);
          children.splice(i, 2);
        } else {
          children.splice(i, 1);
        }
        i -= 1;
      } else if (nextIsContinuation && next.children) {
        if (!CLOSERS.test(nextText)) next.children.unshift({ type: "text", value: " " });
        next.children.unshift(chip);
        children.splice(i, 1);
        i -= 1;
      } else {
        children[i] = { type: "paragraph", children: [chip] };
      }
    }
  };
  return (tree: MdastNode): void => {
    visit(tree);
  };
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ children, className = "" }) => {
  const containerClassName = `markdown-content ${className}`.trim();

  // Pre-process cite tags and doc-cite tags before rendering
  const processedContent = React.useMemo(
    () => preprocessDocCiteTags(preprocessCiteTags(children)),
    [children]
  );

  React.useEffect(() => {
    // Check if styles are already added to avoid duplicates
    if (document.querySelector("#markdown-renderer-styles")) {
      return;
    }

    const styleSheet = document.createElement("style");
    styleSheet.id = "markdown-renderer-styles";
    styleSheet.innerHTML = `
      /* Base markdown styles - headers, paragraphs, lists */
      .markdown-content h1 {
        font-size: 24px;
        font-weight: 600;
        margin: 20px 0;
        color: #333;
      }
      
      .markdown-content h2 {
        font-size: 20px;
        font-weight: 600;
        margin: 24px 0 16px 0;
      }
      
      .markdown-content h3 {
        font-size: 18px;
        font-weight: 600;
        margin: 20px 0 12px 0;
      }
      
      .markdown-content h4 {
        font-size: 14px;
        font-weight: 600;
        margin: 16px 0 8px 0;
      }

      .markdown-content p {
        margin-bottom: 0.5rem;
        line-height: 1.5;
      }
      
      .markdown-content ul {
        padding-left: 24px;
        margin: 8px 0;
        list-style-type: circle;
      }
      
      .markdown-content ol {
        padding-left: 24px;
        margin: 8px 0;
      }
      
      .markdown-content li {
        margin-bottom: 8px;
        line-height: 1.4;
      }
      
      /* Inline code and pre styles removed - now handled by CodeBlock component */

      /* Main content container - clean and scrolling-neutral */
      .markdown-content {
        /* Smooth scrolling optimization for mobile devices */
        -webkit-overflow-scrolling: touch;
        /* Professional scrollbar styling when scrolling does occur */
        scrollbar-width: thin;
        /* Critically important: NO overflow-x auto here to prevent entire content scrolling */
      }

      /* Table scrolling container - the heart of our isolated scrolling solution */
      .markdown-content table {
        /* Block display enables precise overflow control */
        display: block;
        overflow-x: auto;
        overflow-y: visible;
        
        /* Key architectural decision: no width constraints on the container */
        /* This allows the container to expand to accommodate table content */
        /* while still providing horizontal scrolling when needed */
        
        /* Visual presentation for the scrolling container */
        margin: 16px 0;
        border-radius: 6px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        
        /* Professional scrollbar styling for individual tables */
        scrollbar-width: thin;
      }

      /* Custom scrollbar design for table containers */
      .markdown-content table::-webkit-scrollbar {
        height: 6px;
      }

      .markdown-content table::-webkit-scrollbar-track {
        background: #f1f3f4;
        border-radius: 3px;
      }

      .markdown-content table::-webkit-scrollbar-thumb {
        background: #c1c8cd;
        border-radius: 3px;
        transition: background-color 0.2s ease;
      }

      .markdown-content table::-webkit-scrollbar-thumb:hover {
        background: #a8b2b9;
      }

      /* Internal table structure - where the real table magic happens */
      .markdown-content table > * {
        /* Restore proper table display behavior for internal elements */
        // display: table;
        
        /* Content width management - this is where we set size requirements */
        min-width: 600px;
        width: max-content;
        
        /* Essential table styling for professional appearance */
        border-collapse: collapse;
        font-family: 'Segoe UI', sans-serif;
        font-size: 14px;
        table-layout: auto; /* Changed to auto for better column width distribution */
        background: white;
      }

      /* Table header styling - creates clear visual hierarchy */
      .markdown-content thead {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      }

      .markdown-content th {
        padding: 12px 16px;
        text-align: left;
        font-weight: 600;
        color: #495057;
        border-bottom: 2px solid #dee2e6;
        position: relative;
        /* Uniform width constraints matching cells */
        min-width: 120px;
        max-width: 350px;
        /* Allow headers to wrap gracefully when needed */
        white-space: normal;
        /* Optimize line height for potentially multi-line headers */
        line-height: 1.3;
        /* Set minimum height to maintain visual consistency */
        min-height: 40px;
        /* Ensure text aligns well even when wrapping */
        vertical-align: top;
      }

      /* Add subtle separator between header columns */
      .markdown-content th:not(:last-child)::after {
        content: '';
        position: absolute;
        right: 0;
        top: 25%;
        bottom: 25%;
        width: 1px;
        background-color: #dee2e6;
      }

      /* Table body and row styling - supports easy scanning */
      .markdown-content tbody tr {
        border-bottom: 1px solid #f1f3f4;
        transition: background-color 0.2s ease;
      }

      /* Alternating row colors for better readability */
      .markdown-content tbody tr:nth-child(even) {
        background-color: #fafbfc;
      }

      /* Hover effect for interactive feel */
      .markdown-content tbody tr:hover {
        background-color: #f8f9fa;
      }

      /* Table cell styling - optimized for contract data */
      .markdown-content td {
        padding: 12px 16px;
        color: #333;
        line-height: 1.4;
        vertical-align: top;
        border-right: 1px solid #f1f3f4;
        /* Uniform width constraints for all columns */
        min-width: 120px;
        max-width: 350px;
        /* Allow text to wrap for better readability */
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;
        /* Ensure content is visible */
        overflow: visible;
      }

      /* Remove border from last column for clean appearance */
      .markdown-content td:last-child,
      .markdown-content th:last-child {
        border-right: none;
      }

      /* Responsive behavior that works consistently with our architecture */
      @media (max-width: 600px) {
        /* Apply responsive sizing to the internal table structure */
        .markdown-content table > * {
          font-size: 13px;
          /* Reduce minimum width for narrow screens while maintaining readability */
          min-width: 550px;
        }
        
        /* Adjust cell padding to maximize space utilization */
        .markdown-content th,
        .markdown-content td {
          padding: 8px 12px;
        }
      }

      @media (max-width: 400px) {
        /* Further responsive adjustments for very narrow screens */
        .markdown-content table > * {
          font-size: 12px;
          /* Minimum width that still preserves content accessibility */
          min-width: 500px;
        }
        
        /* Tighter padding for maximum space efficiency */
        .markdown-content th,
        .markdown-content td {
          padding: 6px 8px;
        }
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      const existingStyle = document.querySelector("#markdown-renderer-styles");
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  // Memoize components to prevent CitationBubble remounting during streaming.
  // Without this, each content update creates a new components object reference,
  // causing ReactMarkdown to remount all custom components and breaking onMouseLeave.
  const components = React.useMemo(
    () => ({
      table: ({ children }: { children?: React.ReactNode }) => (
        <TableWithExport>{children}</TableWithExport>
      ),
      // Force all markdown links to open in a new window. The Office add-in
      // taskpane runs in an iframe; many sites (e.g. catylexdemo.catylex.net)
      // refuse to be embedded via X-Frame-Options and the default in-pane
      // navigation breaks them.
      a: ({
        href,
        children,
        ...rest
      }: {
        href?: string;
        children?: React.ReactNode;
      } & React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
        <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ),
      // react-markdown no longer passes an `inline` flag to the `code`
      // component, so inline-vs-block is distinguished structurally instead:
      // block code (fenced/indented) always arrives wrapped as
      // <pre><code>…</code></pre>, while inline code is a bare <code>.
      // The <pre> override unwraps its inner code element and renders the
      // full block treatment; the <code> override renders the inline chip.
      pre: ({ children }: { children?: React.ReactNode }) => {
        const codeEl = React.Children.toArray(children).find(React.isValidElement) as
          | React.ReactElement<{ className?: string; children?: React.ReactNode }>
          | undefined;
        if (codeEl) {
          return <CodeBlock className={codeEl.props.className}>{codeEl.props.children}</CodeBlock>;
        }
        return <CodeBlock>{children}</CodeBlock>;
      },
      code: ({ className, children }: { className?: string; children?: React.ReactNode }) => (
        <CodeBlock inline className={className}>
          {children}
        </CodeBlock>
      ),
      // Citation component for web search results
      // Renders <cite source="Source Name" url="..."/> as CitationBubble
      // URL is provided directly by the LLM via the url attribute
      // "data-sources" is set by rehypeGroupWebCites on the first tag of a run
      // of adjacent tags, listing every source in the run.
      cite: (props: { source?: string; url?: string; children?: React.ReactNode }) => {
        const { source, url, children } = props;
        const attributes = props as Record<string, unknown>;
        const grounded = attributes["data-grounded"] === "true";
        // Only for tags spliced in from Gemini's grounding: a model writing these
        // attributes itself must not change how its citation renders.
        const group = grounded ? parseWebCiteGroup(attributes["data-sources"]) : undefined;
        const pageTitle =
          grounded && typeof attributes["data-title"] === "string"
            ? attributes["data-title"]
            : undefined;

        // Log warnings for missing/invalid citation attributes (for monitoring)
        if (!source) {
          log.warn("Citation missing 'source' attribute", { url, hasChildren: !!children });
        }
        if (!url) {
          log.warn("Citation missing 'url' attribute", { source });
        }
        return (
          <CitationBubble
            source={source || "Source"}
            url={url}
            group={group}
            grounded={grounded}
            pageTitle={pageTitle}
          >
            {children}
          </CitationBubble>
        );
      },
      // Document citation tag — routes to playbook or generic bubble based on
      // citation source (looked up via DocumentCitationContext).
      // Note: hast-util-to-jsx-runtime passes data-* attributes as raw HTML
      // attribute names (e.g., "data-ref") for custom elements, not camelCase,
      // because they have no schema space. Must use bracket notation.
      // "data-refs" is set by rehypeGroupDocCites on the first tag of a run of
      // adjacent tags, listing every ref in the run.
      "doc-cite": (props: Record<string, unknown>) => {
        const citationRef = String(props["data-ref"] ?? "");
        const refs = props["data-refs"];
        const groupRefs = typeof refs === "string" ? refs.split(" ") : undefined;

        return <DocCiteRouter citationRef={citationRef} groupRefs={groupRefs} />;
      },
    }),
    [] // Empty deps - component definitions are stable
  );

  return (
    <div className={containerClassName}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkInlineShortCodeBlocks]}
        rehypePlugins={[
          rehypeRaw,
          rehypeStripReservedProps,
          rehypeGroupDocCites,
          rehypeGroupWebCites,
        ]}
        components={components}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

// Memoized because react-markdown re-runs the full remark/rehype parse on every
// render and parents re-render on every streamed chunk. Both props are strings,
// so the shallow compare is an exact content check: unchanged content (completed
// messages, mounted tool payloads like multi-megabyte playbooks) is never
// re-parsed, while actively streaming segments still re-render on each chunk.
export default React.memo(MarkdownRenderer);
